import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Estado de una lista guardado en la URL (?q=...&page=2).
 *
 * Con useState la búsqueda, los filtros y la página se perdían al entrar a un
 * detalle y volver. En la URL sobreviven al "atrás" y se pueden compartir.
 * Usa replace para no llenar el historial con cada tecla.
 */
export function useUrlState(key: string, defaultValue = ''): [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams()
  const value = searchParams.get(key) ?? defaultValue

  const setValue = useCallback(
    (next: string) => {
      // Se parte de la URL actual y no del `prev` del hook: los buscadores con
      // debounce guardan este setter desde el primer render y con `prev`
      // borrarían los filtros puestos después.
      const params = new URLSearchParams(window.location.search)
      if (next === '' || next === defaultValue) params.delete(key)
      else params.set(key, next)
      // Cambiar un filtro vuelve a la página 1. Va aquí y no en un segundo
      // setPage(1), que escribiría la URL dos veces.
      if (key !== 'page') params.delete('page')
      setSearchParams(params, { replace: true })
    },
    [key, defaultValue, setSearchParams]
  )

  return [value, setValue]
}

/** Página de una lista paginada en la URL; vuelve a 1 si el valor no es válido. */
export function useUrlPage(key = 'page'): [number, (page: number) => void] {
  const [raw, setRaw] = useUrlState(key, '1')
  const page = Math.max(1, parseInt(raw, 10) || 1)
  const setPage = useCallback((next: number) => setRaw(String(next)), [setRaw])
  return [page, setPage]
}
